"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { aprilProposal } from "../data/april";
import { PROPOSAL_FONTS_HREF, proposalDisplay, proposalFont } from "../proposalTheme";

const proposal = aprilProposal;

const ink = "#241F1A";
const cream = "#F7F4ED";
const paper = "#EFE8DC";
const gold = "#8C6239";
const white = "#FFFFFF";
const muted = "#5C564E";

export default function AprilProposalPage() {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${PROPOSAL_FONTS_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PROPOSAL_FONTS_HREF;
    document.head.appendChild(link);
  }, []);

  return (
    <Box component="main" sx={{ bgcolor: cream, color: ink, minHeight: "100vh" }}>
      <Helmet>
        <title>Guggenheim Securities · A private comedy night | ComedyUO</title>
        <meta
          name="description"
          content="A private comedy night for April at Guggenheim Securities. Three comics, one emcee, and a standard food package."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta property="og:title" content="Guggenheim Securities · A private comedy night | ComedyUO" />
        <meta
          property="og:description"
          content="A private comedy night for April at Guggenheim Securities."
        />
        <meta property="og:image" content={proposal.heroImage} />
        <meta property="og:image:alt" content="A packed crowd at a ComedyUO show" />
      </Helmet>

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          px: { xs: 3, md: 6 },
          height: { xs: 64, md: 76 },
          bgcolor: ink,
        }}
      >
        <Box
          component="img"
          src={proposal.comedyuoLogo}
          alt="ComedyUO"
          sx={{ height: { xs: 28, md: 36 }, width: "auto" }}
        />
        <Typography
          sx={{
            fontFamily: proposalFont,
            fontSize: { xs: "0.68rem", md: "0.78rem" },
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: white,
            textAlign: "right",
          }}
        >
          Guggenheim Securities
        </Typography>
      </Box>

      <Box sx={{ position: "relative", minHeight: { xs: "68vh", md: "78vh" }, display: "flex", alignItems: "flex-end" }}>
        <Box
          component="img"
          src={proposal.heroImage}
          alt={proposal.heroImageAlt}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 55%" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(36,31,26,0.15) 0%, rgba(36,31,26,0.82) 100%)",
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 1120, mx: "auto", px: { xs: 3, md: 6 }, pb: { xs: 6, md: 8 }, pt: 10 }}>
          <Typography
            sx={{
              fontFamily: proposalFont,
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ink,
              bgcolor: white,
              display: "inline-block",
              px: 1.5,
              py: 0.75,
              mb: 2.5,
            }}
          >
            {proposal.heroEyebrow}
          </Typography>
          <Typography
            component="h1"
            sx={{ fontFamily: proposalDisplay, fontWeight: 500, color: white, fontSize: { xs: "2.8rem", md: "4.6rem" }, lineHeight: 1.02, maxWidth: 760, mb: 2 }}
          >
            {proposal.heroHeadline}
          </Typography>
          <Typography sx={{ fontFamily: proposalFont, color: "rgba(255,255,255,0.92)", fontSize: { xs: "1.05rem", md: "1.25rem" }, maxWidth: 560, lineHeight: 1.5 }}>
            {proposal.heroSubheadline}
          </Typography>
        </Box>
      </Box>

      <Section>
        <Eyebrow>The night</Eyebrow>
        <Heading>Comedy Built for Guggenheim</Heading>
        <Body>{proposal.intro}</Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 2 }}>
          {proposal.show.map((item) => (
            <Box key={item} sx={{ bgcolor: white, borderTop: `3px solid ${gold}`, px: 2.25, py: 2.25 }}>
              <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.35rem", md: "1.6rem" }, lineHeight: 1.15 }}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <SizzleReel />

      <Section tone="paper">
        <Eyebrow>Food</Eyebrow>
        <Heading>{proposal.food.title}</Heading>
        <Body>{proposal.food.body}</Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 3 } }}>
          {proposal.food.images.map((image) => (
            <Box
              key={image.src}
              component="img"
              src={image.src}
              alt={image.alt}
              sx={{ width: "100%", height: { xs: 240, md: 360 }, objectFit: "cover", display: "block" }}
            />
          ))}
        </Box>
      </Section>

      <Section>
        <Eyebrow>Rooms</Eyebrow>
        <Heading>Five options.</Heading>
        <Body>
          For 40–50 guests. Each price is all in, including taxes, gratuity, and the rest of the usual add-ons.
        </Body>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 3, md: 4 } }}>
          {proposal.venues.map((venue) => (
            <Box key={venue.name} sx={{ bgcolor: white, border: "1px solid rgba(36,31,26,0.12)" }}>
              <Box
                component="img"
                src={venue.image}
                alt={venue.imageAlt}
                sx={{ width: "100%", height: { xs: 210, md: 260 }, objectFit: "cover", display: "block" }}
              />
              <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography sx={{ fontFamily: proposalFont, fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, mb: 1 }}>
                  {venue.neighborhood}
                  {venue.note ? ` · ${venue.note}` : ""}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "baseline", mb: 1.25 }}>
                  <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.7rem", md: "2rem" }, lineHeight: 1.1 }}>{venue.name}</Typography>
                  {venue.price ? (
                    <Typography sx={{ fontFamily: proposalDisplay, fontSize: { xs: "1.7rem", md: "2rem" }, color: gold, whiteSpace: "nowrap" }}>{venue.price}</Typography>
                  ) : null}
                </Box>
                <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "1rem", lineHeight: 1.6 }}>{venue.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="ink">
        <Eyebrow onDark>Run of show</Eyebrow>
        <Heading onDark>How the night moves.</Heading>
        <Body onDark>Times can shift. The shape of the night stays the same.</Body>
        {proposal.runOfShow.map((step, i) => (
          <Box
            key={step.title}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "72px 1fr", md: "120px 1fr" },
              gap: { xs: 2, md: 4 },
              py: 3,
              borderTop: "1px solid rgba(255,255,255,0.18)",
              borderBottom: i === proposal.runOfShow.length - 1 ? "1px solid rgba(255,255,255,0.18)" : "none",
            }}
          >
            <Typography sx={{ fontFamily: proposalFont, fontWeight: 700, color: white, fontSize: "1.1rem" }}>{step.time}</Typography>
            <Box>
              <Typography sx={{ fontFamily: proposalDisplay, color: white, fontSize: { xs: "1.45rem", md: "1.8rem" }, mb: 0.75 }}>{step.title}</Typography>
              <Typography sx={{ fontFamily: proposalFont, color: "rgba(255,255,255,0.82)", fontSize: "1.02rem", lineHeight: 1.55, maxWidth: 640 }}>{step.body}</Typography>
            </Box>
          </Box>
        ))}
      </Section>

      <Section>
        <Eyebrow>Companies</Eyebrow>
        <Heading>Nights we have already made.</Heading>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: 3 }, alignItems: "center", mb: 5 }}>
          {proposal.logos.map((logo) => {
            const large = "large" in logo && logo.large;
            return (
              <Box
                key={logo.alt}
                component="img"
                src={logo.src}
                alt={logo.alt}
                sx={{
                  height: large ? { xs: 78, md: 108 } : { xs: 36, md: 46 },
                  width: "auto",
                  maxWidth: large ? 340 : 168,
                  objectFit: "contain",
                  mixBlendMode: "multiply",
                  bgcolor: "transparent",
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          {proposal.stories.map((story) => (
            <Box key={story.title} sx={{ bgcolor: white, px: 2.5, py: 2.5 }}>
              <Typography sx={{ fontFamily: proposalDisplay, fontSize: "1.45rem", mb: 1 }}>{story.title}</Typography>
              <Typography sx={{ fontFamily: proposalFont, color: muted, fontSize: "1rem", lineHeight: 1.6 }}>{story.body}</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Section tone="paper">
        <Eyebrow>Next</Eyebrow>
        <Heading>Pick a room and a date.</Heading>
        <Body>We will hold the lineup and confirm the room. The prices above are the all-in number for this shape of night.</Body>
        <Typography sx={{ fontFamily: proposalDisplay, fontSize: "1.7rem", mb: 0.5 }}>{proposal.contactName}</Typography>
        <Typography sx={{ fontFamily: proposalFont, color: muted, mb: 2 }}>{proposal.contactRole}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Link
            href={`mailto:${proposal.contactEmail}?subject=${encodeURIComponent("Guggenheim Securities private comedy night")}`}
            underline="none"
            sx={{ fontFamily: proposalFont, fontWeight: 700, color: white, bgcolor: ink, px: 2, py: 1.15 }}
          >
            {proposal.contactEmail}
          </Link>
          <Link href="tel:+13104698961" underline="none" sx={{ fontFamily: proposalFont, fontWeight: 700, color: ink, border: `1px solid ${ink}`, px: 2, py: 1.15 }}>
            {proposal.contactPhone}
          </Link>
        </Box>
      </Section>

      <Box sx={{ px: { xs: 3, md: 6 }, py: 3, bgcolor: ink }}>
        <Typography sx={{ fontFamily: proposalFont, fontSize: "0.85rem", color: "rgba(255,255,255,0.75)" }}>
          Prepared by ComedyUO · Confidential · Guggenheim Securities
        </Typography>
      </Box>
    </Box>
  );
}

function SizzleReel() {
  const ref = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.6 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const src = playing
    ? "https://www.youtube.com/embed/aDvTfp_YtRA?autoplay=1&mute=1&playsinline=1&rel=0"
    : "https://www.youtube.com/embed/aDvTfp_YtRA?rel=0";

  return (
    <Box ref={ref} sx={{ bgcolor: ink, px: { xs: 0, md: 6 }, py: { xs: 0, md: 8 } }}>
      <Box sx={{ maxWidth: 1120, mx: "auto", position: "relative", pt: "56.25%" }}>
        <Box
          key={playing ? "playing" : "paused"}
          component="iframe"
          src={src}
          title="ComedyUO sizzle"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    </Box>
  );
}

function Section({ children, tone = "cream" }: { children: ReactNode; tone?: "cream" | "paper" | "ink" }) {
  const bg = tone === "ink" ? ink : tone === "paper" ? paper : cream;
  return (
    <Box component="section" sx={{ bgcolor: bg, px: { xs: 3, md: 6 }, py: { xs: 7, md: 10 } }}>
      <Box sx={{ maxWidth: 1120, mx: "auto" }}>{children}</Box>
    </Box>
  );
}

function Eyebrow({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: onDark ? "rgba(255,255,255,0.7)" : gold,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function Heading({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontFamily: proposalDisplay,
        fontWeight: 500,
        fontSize: { xs: "2.1rem", md: "3rem" },
        lineHeight: 1.05,
        color: onDark ? white : ink,
        mb: 2,
        maxWidth: 740,
      }}
    >
      {children}
    </Typography>
  );
}

function Body({ children, onDark = false }: { children: ReactNode; onDark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: proposalFont,
        fontSize: { xs: "1.05rem", md: "1.15rem" },
        lineHeight: 1.6,
        color: onDark ? "rgba(255,255,255,0.82)" : muted,
        maxWidth: 680,
        mb: 4,
      }}
    >
      {children}
    </Typography>
  );
}
